import React, { useEffect, useRef, useState } from "react";
import { Control, Controller, FieldErrors, useFormContext } from "react-hook-form";
import { Box, TextField } from "@mui/material";
import { formErrorRules } from "../shared/utils/form-error-rules";

interface PinInputProps {
    device?: "desktop" | "mobile";
    name: string;
    control: Control;
    disabled?: boolean;
    errors: FieldErrors;
    gutterBottom?: boolean;
}

const PinInput = ({ name, device = "desktop", control, disabled, errors, gutterBottom }: PinInputProps) => {
    const refs = useRef<React.RefObject<HTMLInputElement>[]>([
        React.createRef(),
        React.createRef(),
        React.createRef(),
        React.createRef(),
        React.createRef(),
        React.createRef(),
    ]);
    const { watch, setValue } = useFormContext();
    const pinValues = watch(["pin0", "pin1", "pin2", "pin3", "pin4", "pin5"]);

    const prevPinValuesRef = useRef<string[]>([]);

    const [enabledIndex, setEnabledIndex] = useState(0);
    const [initializedIndex, setInitializedIndex] = useState(0);

    useEffect(() => {
        const nextSibling = refs.current[enabledIndex];
        if (nextSibling && nextSibling.current) {
            nextSibling.current.focus();
        }
    }, [enabledIndex]);

    useEffect(() => {
        if (JSON.stringify(prevPinValuesRef.current) === JSON.stringify(pinValues)) {
            return;
        }

        prevPinValuesRef.current = pinValues;
        const pinCode = pinValues.join("");
        setValue(name, pinCode);
    }, [pinValues]);

    return (
        <Box marginBottom={gutterBottom ? "1.6rem" : 0} sx={{ gap: "1.6rem" }} display="flex" alignContent="center" justifyContent="center">
            {[1, 2, 3, 4, 5, 6].map((_, index) => (
                <Controller
                    key={`pinKey${index}`}
                    name={`pin${index}`}
                    control={control}
                    defaultValue=""
                    rules={formErrorRules(name)}
                    render={({ field: { onChange, value } }) => (
                        <TextField
                            sx={{
                                "&&": {
                                    width: device === "mobile" ? 50 : 64,
                                    height: device === "mobile" ? 50 : 64,
                                    minWidth: 0,
                                    minHeight: 0,
                                    "& .MuiInputBase-root": {
                                        width: device === "mobile" ? 50 : 64,
                                        height: device === "mobile" ? 50 : 64,
                                    },
                                    "& .MuiInputBase-input": {
                                        width: device === "mobile" ? 50 : 64,
                                        height: device === "mobile" ? 50 : 64,
                                        padding: 0,
                                        textAlign: "center",
                                        fontSize: device === "mobile" ? 24 : 28,
                                    },
                                },
                                "& input[type=number]": {
                                    "-moz-appearance": "textfield",
                                    "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
                                        "-webkit-appearance": "none",
                                        margin: 0,
                                    },
                                },
                            }}
                            type="number"
                            autoComplete="off"
                            error={!!errors[`pin${index}`]}
                            inputProps={{
                                maxLength: 1,
                                pattern: "\\d", // allows only numbers
                            }}
                            disabled={disabled || index > initializedIndex}
                            onChange={(e) => {
                                if (e.target.value === "" || /^[0-9]$/.test(e.target.value)) {
                                    onChange(e);
                                    if (e.target.value === "") {
                                        const prevSibling = refs.current[index - 1];
                                        if (prevSibling && prevSibling.current) {
                                            prevSibling.current.focus();
                                        }
                                    } else if (e.target.value !== "") {
                                        const nextSibling = refs.current[index + 1];
                                        if (nextSibling && nextSibling.current) {
                                            setEnabledIndex(index + 1);
                                            setInitializedIndex((prevState) => Math.max(prevState, index + 1));
                                        }
                                    }
                                }
                            }}
                            value={value}
                            inputRef={refs.current[index]}
                        />
                    )}
                />
            ))}
        </Box>
    );
};

export default PinInput;
